import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  FormLabel,
  Heading,
  IconButton,
  Input,
  SimpleGrid,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { MdAdd, MdEdit } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import {
  AppCard,
  AppSelect,
  OutlineButton,
  PrimaryButton,
  SortableTh,
  StatusBadge,
  TablePagination,
  TableLoader,
} from '../../../../components/ui';
import {
  formatGender,
  GENDER_OPTIONS,
  VERIFICATION_OPTIONS,
} from '../../../../data/master/people';
import { findVillageById } from '../../../../data/master/villages';
import { usePagination } from '../../../../hooks/usePagination';
import { useTableSort } from '../../../../hooks/useTableSort';
import { lookupEquipmentTypes, lookupVillages } from '../../../../repositories/lookup.repository';
import { listVillagers } from '../../../../repositories/villager.repository';

import { VillagerFormModal } from './VillagerFormModal';

function statusBadgeProps(status) {
  if (status === 'Verified') return { status: 'verified' };
  if (status === 'Rejected') return { status: 'error' };
  return { status: 'pending' };
}

function personEquipments(person) {
  return Array.isArray(person?.equipments) ? person.equipments : [];
}

function EquipmentCountCell({ equipments }) {
  const count = equipments.length;
  if (!count) {
    return (
      <Text fontWeight="600" fontSize={{ base: '14px', xl: '15px' }}>
        0
      </Text>
    );
  }

  return (
    <Tooltip
      hasArrow
      placement="top"
      openDelay={200}
      label={
        <Box>
          {equipments.map((e, i) => (
            <Text key={`${e.name}-${e.regNo}-${i}`} fontSize="13px" lineHeight="1.5">
              {e.name}
              {e.regNo ? ` · ${e.regNo}` : ''}
            </Text>
          ))}
        </Box>
      }
    >
      <Text
        as="span"
        fontWeight="600"
        fontSize={{ base: '14px', xl: '15px' }}
        cursor="help"
        borderBottomWidth="1px"
        borderBottomStyle="dashed"
        borderColor="border.subtle"
      >
        {count}
      </Text>
    </Tooltip>
  );
}

export default function VillagersPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const formModal = useDisclosure();
  const [villages, setVillages] = useState([]);
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPerson, setEditingPerson] = useState(null);
  const [search, setSearch] = useState('');
  const [genderFilter, setGenderFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [equipmentTypeFilter, setEquipmentTypeFilter] = useState('');
  const [villageFilter, setVillageFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [peopleRes, villageRes, typeRes] = await Promise.all([
        listVillagers({ pageSize: 200 }),
        lookupVillages(),
        lookupEquipmentTypes(),
      ]);
      setPeople(peopleRes.items || []);
      setVillages(villageRes || []);
      setEquipmentTypes(typeRes || []);
    } catch (err) {
      toast({
        title: err?.message || 'Failed to load villagers',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const equipmentTypeOptions = useMemo(
    () =>
      equipmentTypes
        .filter((e) => e.active !== false)
        .map((e) => ({ value: e.name, label: e.name })),
    [equipmentTypes],
  );

  const villageFilterOptions = useMemo(
    () =>
      [...villages]
        .sort((a, b) => a.villageName.localeCompare(b.villageName))
        .map((v) => ({
          value: v.villageId,
          label: v.villageName,
          description: `${v.talukaName} · ${v.pin}`,
        })),
    [villages],
  );

  const pendingCount = useMemo(
    () =>
      people.filter((p) => (p.verificationStatus || 'Pending') === 'Pending').length,
    [people],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return people.filter((p) => {
      const matchQ =
        !q ||
        (p.fullName || '').toLowerCase().includes(q) ||
        (p.peopleId || '').toLowerCase().includes(q) ||
        (p.mobile || '').includes(q) ||
        (p.name || '').toLowerCase().includes(q);
      const matchG = !genderFilter || p.gender === genderFilter;
      const status = p.verificationStatus || 'Pending';
      const matchS = !statusFilter || status === statusFilter;
      const matchV = !villageFilter || p.villageId === villageFilter;
      const personAssets = personEquipments(p);
      const matchEq =
        !equipmentTypeFilter ||
        personAssets.some((a) => a.equipmentType === equipmentTypeFilter);
      return matchQ && matchG && matchS && matchV && matchEq;
    });
  }, [
    people,
    search,
    genderFilter,
    statusFilter,
    villageFilter,
    equipmentTypeFilter,
  ]);

  const { sorted, sort, toggleSort } = useTableSort(filtered, {
    peopleId: (p) => p.peopleId,
    fullName: (p) => p.fullName,
    mobile: (p) => p.mobile,
    village: (p) => findVillageById(villages, p.villageId)?.villageName || p.villageId,
    gender: (p) => formatGender(p.gender),
    equipments: (p) => personEquipments(p).length,
    status: (p) => p.verificationStatus || 'Pending',
  });

  const {
    pageItems,
    page,
    pageSize,
    totalCount,
    totalPages,
    setPage,
    setPageSize,
  } = usePagination(sorted);

  const openAdd = () => {
    setEditingPerson(null);
    formModal.onOpen();
  };

  const openEdit = (p) => {
    setEditingPerson(p);
    formModal.onOpen();
  };

  const clearFilters = () => {
    setSearch('');
    setGenderFilter('');
    setStatusFilter('');
    setVillageFilter('');
    setEquipmentTypeFilter('');
  };

  return (
    <Box>
      <Flex
        justify="space-between"
        align={{ base: 'start', md: 'center' }}
        gap={4}
        mb={6}
        wrap="wrap"
      >
        <Box>
          <Flex align="center" gap={3} wrap="wrap" mb={1}>
            <Heading size={{ base: 'lg', xl: 'xl' }}>Villagers</Heading>
            <StatusBadge status={pendingCount > 0 ? 'pending' : 'neutral'}>
              {pendingCount} pending
            </StatusBadge>
          </Flex>
          <Text color="text.secondary" mt={2} maxW="640px" fontSize={{ base: '14px', xl: '15px' }}>
            People Master — click a row for full details and equipment.
          </Text>
        </Box>
        <PrimaryButton size="sm" leftIcon={<MdAdd />} onClick={openAdd}>
          Add villager
        </PrimaryButton>
      </Flex>

      <AppCard mb={4}>
        <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4, xl: 7 }} spacing={3} alignItems="end">
          <Box>
            <FormLabel fontSize="13px" mb={2}>
              Search
            </FormLabel>
            <Input
              h="40px"
              fontSize="14px"
              placeholder="Name / ID / mobile"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Box>
          <AppSelect
            label="Village"
            labelFontSize="13px"
            h="40px"
            placeholder="All villages"
            value={villageFilter}
            onChange={(v) => setVillageFilter(v || '')}
            options={[{ value: '', label: 'All villages' }, ...villageFilterOptions]}
            isSearchable
            searchPlaceholder="Search village / PIN…"
          />
          <AppSelect
            label="Gender"
            labelFontSize="13px"
            h="40px"
            placeholder="All genders"
            value={genderFilter}
            onChange={(v) => setGenderFilter(v || '')}
            options={[{ value: '', label: 'All genders' }, ...GENDER_OPTIONS]}
          />
          <AppSelect
            label="Verification"
            labelFontSize="13px"
            h="40px"
            value={statusFilter}
            onChange={(v) => setStatusFilter(v ?? '')}
            options={[
              { value: '', label: 'All statuses' },
              ...VERIFICATION_OPTIONS,
            ]}
          />
          <AppSelect
            label="Equipment type"
            labelFontSize="13px"
            h="40px"
            placeholder="All types"
            value={equipmentTypeFilter}
            onChange={(v) => setEquipmentTypeFilter(v || '')}
            options={[{ value: '', label: 'All types' }, ...equipmentTypeOptions]}
            isSearchable
            searchPlaceholder="Search equipment…"
          />
          <OutlineButton size="sm" w="100%" h="40px" minH="40px" onClick={clearFilters}>
            Clear filters
          </OutlineButton>
        </SimpleGrid>
      </AppCard>

      <AppCard p={0} overflow="hidden" mb={4}>
        {loading ? (
          <TableLoader rows={7} columns={5} />
        ) : (
          <>
            <Box overflowX="auto">
              <Table variant="simple" size="md">
                <Thead bg="bg.input">
                  <Tr>
                    <SortableTh
                      columnKey="peopleId"
                      sort={sort}
                      onSort={toggleSort}
                      fontSize={{ base: '12px', xl: '13px' }}
                    >
                      People ID
                    </SortableTh>
                    <SortableTh
                      columnKey="fullName"
                      sort={sort}
                      onSort={toggleSort}
                      fontSize={{ base: '12px', xl: '13px' }}
                    >
                      Full name
                    </SortableTh>
                    <SortableTh
                      columnKey="mobile"
                      sort={sort}
                      onSort={toggleSort}
                      fontSize={{ base: '12px', xl: '13px' }}
                    >
                      Mobile
                    </SortableTh>
                    <SortableTh
                      columnKey="village"
                      sort={sort}
                      onSort={toggleSort}
                      fontSize={{ base: '12px', xl: '13px' }}
                    >
                      Village
                    </SortableTh>
                    <SortableTh
                      columnKey="gender"
                      sort={sort}
                      onSort={toggleSort}
                      fontSize={{ base: '12px', xl: '13px' }}
                    >
                      Gender
                    </SortableTh>
                    <SortableTh
                      columnKey="equipments"
                      sort={sort}
                      onSort={toggleSort}
                      fontSize={{ base: '12px', xl: '13px' }}
                    >
                      Equipments
                    </SortableTh>
                    <SortableTh
                      columnKey="status"
                      sort={sort}
                      onSort={toggleSort}
                      fontSize={{ base: '12px', xl: '13px' }}
                    >
                      Status
                    </SortableTh>
                    <Th fontSize={{ base: '12px', xl: '13px' }}>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {pageItems.map((p) => {
                    const v = findVillageById(villages, p.villageId);
                    const personAssets = personEquipments(p);
                    const status = p.verificationStatus || 'Pending';
                    return (
                      <Tr
                        key={p.id || p.peopleId}
                        cursor="pointer"
                        _hover={{ bg: 'bg.input' }}
                        onClick={() => navigate(`/admin/equipments/villagers/${p.peopleId}`)}
                      >
                        <Td fontFamily="mono" fontSize={{ base: '13px', xl: '14px' }}>
                          {p.peopleId}
                        </Td>
                        <Td fontWeight="600" fontSize={{ base: '14px', xl: '15px' }}>
                          {p.fullName}
                        </Td>
                        <Td fontSize={{ base: '14px', xl: '15px' }}>{p.mobile}</Td>
                        <Td>
                          <Text fontSize={{ base: '13px', xl: '14px' }} fontWeight="600">
                            {v?.villageName || '—'}
                          </Text>
                          {v ? (
                            <Text fontSize="12px" color="text.secondary">
                              {v.villageId} · {v.pin}
                            </Text>
                          ) : null}
                        </Td>
                        <Td fontSize={{ base: '14px', xl: '15px' }}>{formatGender(p.gender)}</Td>
                        <Td>
                          <EquipmentCountCell equipments={personAssets} />
                        </Td>
                        <Td>
                          <StatusBadge {...statusBadgeProps(status)}>{status}</StatusBadge>
                        </Td>
                        <Td onClick={(e) => e.stopPropagation()}>
                          <IconButton
                            aria-label={`Edit ${p.fullName}`}
                            icon={<MdEdit />}
                            size="sm"
                            variant="ghostInk"
                            onClick={() => openEdit(p)}
                          />
                        </Td>
                      </Tr>
                    );
                  })}
                  {pageItems.length === 0 ? (
                    <Tr>
                      <Td colSpan={8}>
                        <Text
                          color="text.secondary"
                          py={8}
                          textAlign="center"
                          fontSize={{ base: '14px', xl: '15px' }}
                        >
                          No villagers match your filters.
                        </Text>
                      </Td>
                    </Tr>
                  ) : null}
                </Tbody>
              </Table>
            </Box>

            <TablePagination
              page={page}
              pageSize={pageSize}
              totalCount={totalCount}
              totalPages={totalPages}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              px={{ base: 4, xl: 5 }}
            />
          </>
        )}
      </AppCard>

      <VillagerFormModal
        isOpen={formModal.isOpen}
        onClose={formModal.onClose}
        person={editingPerson}
        villages={villages}
        onSaved={load}
      />
    </Box>
  );
}
